import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Tabs, 
  Tab, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { apiService } from '../services/api.service';
import { useTenant } from '../context/TenantContext';

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-management-tabpanel-${index}`}
      aria-labelledby={`user-management-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// User form dialog component
function UserFormDialog({ open, onClose, onSubmit, initialData = null, roles, organizations }) {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    org_id: '',
    roles: [],
    password: ''
  });

  useEffect(() => {
    if (initialData) {
      // Make sure roles is always an array
      const userRoles = Array.isArray(initialData.roles) ? initialData.roles : [];
      
      setFormData({
        ...initialData,
        roles: userRoles,
        password: '' // Don't populate password field
      });
    } else {
      setFormData({
        username: '',
        email: '',
        full_name: '',
        org_id: '',
        roles: [],
        password: ''
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialData ? 'Edit User' : 'Create User'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Full Name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              required
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel>Organization</InputLabel>
              <Select
                name="org_id"
                value={formData.org_id || ''}
                onChange={handleChange}
                label="Organization"
              >
                {organizations.map(org => (
                  <MenuItem key={org.org_id} value={org.org_id}>
                    {org.org_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Roles</InputLabel>
              <Select
                name="roles"
                value={formData.roles || []}
                onChange={handleChange}
                label="Roles"
                multiple
                renderValue={(selected) => selected.join(', ')}
              >
                {roles.map(role => (
                  <MenuItem key={role.role_id} value={role.role_name}>
                    {role.role_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!initialData && (
              <TextField
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                fullWidth
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            {initialData ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// Organization form dialog component
function OrganizationFormDialog({ open, onClose, onSubmit, initialData = null }) {
  const [formData, setFormData] = useState({
    org_name: '',
    org_code: '',
    status: 'ACTIVE'
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        org_name: initialData.org_name || '',
        org_code: initialData.org_code || '',
        status: initialData.status || 'ACTIVE'
      });
    } else {
      setFormData({
        org_name: '',
        org_code: '',
        status: 'ACTIVE'
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialData ? 'Edit Organization' : 'Create Organization'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization Name"
              name="org_name"
              value={formData.org_name}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Organization Code"
              name="org_code"
              value={formData.org_code}
              onChange={handleChange}
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleChange}
                label="Status"
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            {initialData ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// Role form dialog component
function RoleFormDialog({ open, onClose, onSubmit, initialData = null, organizations }) {
  const [formData, setFormData] = useState({
    role_name: '',
    description: '',
    org_id: ''
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        role_name: initialData.role_name || '',
        description: initialData.description || '',
        org_id: initialData.org_id || ''
      });
    } else {
      setFormData({
        role_name: '',
        description: '',
        org_id: ''
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialData ? 'Edit Role' : 'Create Role'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Organization</InputLabel>
              <Select
                name="org_id"
                value={formData.org_id || ''}
                onChange={handleChange}
                label="Organization"
              >
                {organizations.map(org => (
                  <MenuItem key={org.org_id} value={org.org_id}>
                    {org.org_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Role Name"
              name="role_name"
              value={formData.role_name}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={4}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            {initialData ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default function UserManagement() {
  const { tenantAlias } = useTenant();
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dialog states
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [organizationDialogOpen, setOrganizationDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, [tenantAlias]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch users, roles, and organizations in parallel
      const [usersResponse, rolesResponse, organizationsResponse] = await Promise.all([
        apiService.get(`/tenant-users/users`),
        apiService.get(`/tenant-users/roles`),
        apiService.get(`/tenant-users/organizations`)
      ]);
      
      console.log('Users response:', usersResponse);
      console.log('Roles response:', rolesResponse);
      console.log('Organizations response:', organizationsResponse);
      
      // The API is returning arrays directly, not wrapped in a data property
      const users = Array.isArray(usersResponse) ? usersResponse : [];
      const roles = Array.isArray(rolesResponse) ? rolesResponse : [];
      const organizations = Array.isArray(organizationsResponse) ? organizationsResponse : [];
      
      console.log('Processed users:', users);
      console.log('Processed roles:', roles);
      console.log('Processed organizations:', organizations);
      
      setUsers(users);
      setRoles(roles);
      setOrganizations(organizations);
    } catch (err) {
      setError('Failed to load data. Please try again later.');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // User management handlers
  const handleCreateUser = () => {
    setSelectedItem(null);
    setUserDialogOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedItem(user);
    setUserDialogOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await apiService.delete(`/tenant-users/users/${userId}`);
        fetchData();
      } catch (err) {
        setError('Failed to delete user. Please try again later.');
        console.error('Error deleting user:', err);
      }
    }
  };

  const handleUserSubmit = async (formData) => {
    try {
      // Prepare the data for the API
      const userData = {
        username: formData.username,
        email: formData.email,
        full_name: formData.full_name,
        org_id: formData.org_id,
        roles: formData.roles,
        status: 'ACTIVE'
      };
      
      // Add password only for new users
      if (!selectedItem && formData.password) {
        userData.password = formData.password;
      }
      
      if (selectedItem) {
        await apiService.patch(`/tenant-users/users/${selectedItem.user_id}`, userData);
      } else {
        await apiService.post('/tenant-users/users', userData);
      }
      setUserDialogOpen(false);
      fetchData();
    } catch (err) {
      setError('Failed to save user. Please try again later.');
      console.error('Error saving user:', err);
    }
  };

  // Organization management handlers
  const handleCreateOrganization = () => {
    setSelectedItem(null);
    setOrganizationDialogOpen(true);
  };

  const handleEditOrganization = (organization) => {
    setSelectedItem(organization);
    setOrganizationDialogOpen(true);
  };

  const handleDeleteOrganization = async (organizationId) => {
    if (window.confirm('Are you sure you want to delete this organization?')) {
      try {
        await apiService.delete(`/tenant-users/organizations/${organizationId}`);
        fetchData();
      } catch (err) {
        setError('Failed to delete organization. Please try again later.');
        console.error('Error deleting organization:', err);
      }
    }
  };

  const handleOrganizationSubmit = async (formData) => {
    try {
      if (selectedItem) {
        await apiService.patch(`/tenant-users/organizations/${selectedItem.org_id}`, formData);
      } else {
        await apiService.post('/tenant-users/organizations', formData);
      }
      setOrganizationDialogOpen(false);
      fetchData();
    } catch (err) {
      setError('Failed to save organization. Please try again later.');
      console.error('Error saving organization:', err);
    }
  };

  // Role management handlers
  const handleCreateRole = () => {
    setSelectedItem(null);
    setRoleDialogOpen(true);
  };

  const handleEditRole = (role) => {
    setSelectedItem(role);
    setRoleDialogOpen(true);
  };

  const handleDeleteRole = async (roleId) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      try {
        await apiService.delete(`/tenant-users/roles/${roleId}`);
        fetchData();
      } catch (err) {
        setError('Failed to delete role. Please try again later.');
        console.error('Error deleting role:', err);
      }
    }
  };

  const handleRoleSubmit = async (formData) => {
    try {
      if (selectedItem) {
        await apiService.patch(`/tenant-users/roles/${selectedItem.role_id}`, formData);
      } else {
        await apiService.post('/tenant-users/roles', formData);
      }
      setRoleDialogOpen(false);
      fetchData();
    } catch (err) {
      setError('Failed to save role. Please try again later.');
      console.error('Error saving role:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          User Management
        </Typography>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="user management tabs">
          <Tab label="Users" />
          <Tab label="Organizations" />
          <Tab label="Roles" />
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Users Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateUser}
          >
            Add User
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Organization</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.user_id}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.full_name}</TableCell>
                  <TableCell>
                    {user.roles && user.roles.length > 0 ? user.roles.join(', ') : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {organizations.find(org => org.org_id === user.org_id)?.org_name || 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleEditUser(user)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDeleteUser(user.user_id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Organizations Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateOrganization}
          >
            Add Organization
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {organizations.map((org) => (
                <TableRow key={org.org_id}>
                  <TableCell>{org.org_name}</TableCell>
                  <TableCell>{org.org_code || ''}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleEditOrganization(org)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDeleteOrganization(org.org_id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Roles Tab */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateRole}
          >
            Add Role
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Organization</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.role_id}>
                  <TableCell>{role.role_name}</TableCell>
                  <TableCell>
                    {organizations.find(org => org.org_id === role.org_id)?.org_name || 'N/A'}
                  </TableCell>
                  <TableCell>{role.description || ''}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleEditRole(role)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDeleteRole(role.role_id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Dialogs */}
      <UserFormDialog
        open={userDialogOpen}
        onClose={() => setUserDialogOpen(false)}
        onSubmit={handleUserSubmit}
        initialData={selectedItem}
        roles={roles}
        organizations={organizations}
      />
      
      <OrganizationFormDialog
        open={organizationDialogOpen}
        onClose={() => setOrganizationDialogOpen(false)}
        onSubmit={handleOrganizationSubmit}
        initialData={selectedItem}
      />
      
      <RoleFormDialog
        open={roleDialogOpen}
        onClose={() => setRoleDialogOpen(false)}
        onSubmit={handleRoleSubmit}
        initialData={selectedItem}
        organizations={organizations}
      />
    </Box>
  );
} 